import { PostMessageType, RATING_TYPE } from '@abiglobalhealth/go-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { storeHealthNavigatorSessionFeedback } from '@/api/navigator'
import { Button } from '@/components/Button'
import { Close } from '@/components/icons/Close'
import { Popup } from '@/components/Popup'
import { StarRating } from '@/components/StarRating'
import useNavigator from '@/hooks/useNavigator'
import { usePostMessage } from '@/hooks/usePostMessage'
import { useToast } from '@/hooks/useToast'
import { useUser } from '@/hooks/useUser'
import { isAbortError } from '@/utils/errors'

const SESSION_FEEDBACK_DELAY = 3_000

interface SessionFeedbackProps {
  conversationId: string
  onVisibilityChange?: (visible: boolean) => void
}

export const SessionFeedback = ({ conversationId, onVisibilityChange }: SessionFeedbackProps) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { setNavigator } = useNavigator()
  const { sendPostMessage } = usePostMessage()
  const { show: showToast } = useToast()

  const [showPopup, setShowPopup] = useState(false)
  const [selectedRating, setSelectedRating] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const closePopup = () => setShowPopup(false)

  useEffect(() => {
    onVisibilityChange?.(showPopup)
  }, [showPopup])

  const handleSubmit = async () => {
    if (!selectedRating || isSubmitting) return

    try {
      setIsSubmitting(true)
      abortControllerRef.current = new AbortController()

      const ratingPayload = {
        ratingType: RATING_TYPE.STARS,
        rating: selectedRating,
        comment: comment || undefined
      }

      const response = await storeHealthNavigatorSessionFeedback(
        { uniqueId: user!.data.uniqueId!, conversationId, ...ratingPayload },
        abortControllerRef.current.signal
      )
      setNavigator(response)
      await sendPostMessage(PostMessageType.NAVIGATOR_SESSION_FEEDBACK_SUBMITTED, {
        ...ratingPayload,
        conversationId
      })
      showToast(t('navigator.reply_feedback.modal.toast_message'))
      closePopup()
    } catch (error: unknown) {
      if (!isAbortError(error)) throw error
    } finally {
      setIsSubmitting(false)
      abortControllerRef.current = null
    }
  }

  useEffect(() => {
    console.log('[SessionFeedback] Mounted — will show popup in', SESSION_FEEDBACK_DELAY, 'ms for conversationId:', conversationId)
    const t = setTimeout(() => {
      console.log('[SessionFeedback] Showing popup')
      setShowPopup(true)
    }, SESSION_FEEDBACK_DELAY)
    return () => {
      console.log('[SessionFeedback] Unmounted — clearing timer')
      clearTimeout(t)
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [])

  const showLowRatingComment = selectedRating !== null && selectedRating < 3

  return (
    <Popup show={showPopup} testId="session-feedback-popup">
      <div className="flex w-full flex-col px-4 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <div className="w-[15px]" />
          <p className="font-bold">{t('navigator.session_feedback.modal.title')}</p>
          <button onClick={closePopup} disabled={isSubmitting} data-testid="session-feedback-close" aria-label={t('button.close')}>
            <Close customClassName="w-[15px] h-[15px]" />
          </button>
        </div>

        <div className="mt-6">
          <StarRating
            selectedRating={selectedRating}
            onSelectRating={setSelectedRating}
            testIdPrefix="session-feedback"
            containerClassName="flex flex-row justify-center gap-3"
          />
        </div>

        {showLowRatingComment && (
          <textarea
            className="animate-fade-in border-gray focus-visible:border-primary mt-6 w-full resize-none rounded-lg border p-3 text-base transition-[border-color] focus-visible:outline-hidden"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('navigator.session_feedback.modal.placeholder')}
            autoFocus
          />
        )}

        <div className="mt-[30px]">
          <Button
            onClick={handleSubmit}
            disabled={!selectedRating || isSubmitting}
            loading={isSubmitting}
            hasShadow={false}
            data-testid="session-feedback-submit"
            customClassName="w-full py-3.5 px-2.5"
          >
            {t('button.submit')}
          </Button>
        </div>
      </div>
    </Popup>
  )
}
